import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Flower2, Info } from "lucide-react";
import { useFetch } from "../lib/useData";
import { useSettings } from "../context/SettingsContext";
import { PageHero, Section, Placeholder } from "../components/public/PublicLayout";

const HERO = "https://images.unsplash.com/photo-1718476971217-677d43112daa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const money = (s, v) => `${s?.currency_symbol || "₹"}${v}`;

export function SevaCatalogue() {
  const s = useSettings();
  const { data: cats } = useFetch("/public/seva-categories");
  const { data: sevas } = useFetch("/public/sevas");
  const [active, setActive] = useState("all");
  const filtered = (sevas || []).filter((x) => active === "all" || x.category_id === active);

  return (
    <div>
      <PageHero title="Seva & Pooja" subtitle="Offer a sacred seva at the temple" image={HERO} />
      <Section>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="seva-category-filter">
          <button onClick={() => setActive("all")} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active === "all" ? "bg-orange-600 text-white" : "bg-amber-100 text-stone-700 hover:bg-amber-200"}`} data-testid="cat-all">All</button>
          {(cats || []).map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active === c.id ? "bg-orange-600 text-white" : "bg-amber-100 text-stone-700 hover:bg-amber-200"}`} data-testid={`cat-${c.slug}`}>{c.name}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="seva-grid">
          {filtered.map((seva) => (
            <div key={seva.id} className="group bg-white rounded-2xl overflow-hidden border border-amber-200/70 hover:shadow-lg transition-all flex flex-col">
              <div className="h-48 overflow-hidden"><img src={seva.image} alt={seva.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-5 flex flex-col flex-1">
                <span className="text-xs font-semibold text-amber-700">{seva.category_name}</span>
                <h3 className="font-display text-xl font-bold text-stone-900 mt-1">{seva.name}</h3>
                <p className="text-sm text-stone-500 mt-2 line-clamp-2 flex-1">{seva.short_description}</p>
                <div className="flex items-center gap-2 text-xs text-stone-500 mt-3"><Clock className="w-3.5 h-3.5" /> {seva.duration}</div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xl font-extrabold text-orange-700">{money(s, seva.price)}</span>
<Link to={`/seva/${seva.slug || seva.id}`} className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold px-4 py-2 rounded-full transition-colors" data-testid={`seva-view-${seva.slug || seva.id}`}>View & Book</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function SevaDetail() {
  const s = useSettings();
  const { slug } = useParams();
  const { data: seva, loading } = useFetch(`/public/sevas/${slug}`, [slug]);
  if (loading) return <Section>Loading…</Section>;
  if (!seva) return <Section>Seva not found.</Section>;
  return (
    <div>
      <Section>
        <Link to="/seva" className="inline-flex items-center gap-1 text-orange-700 font-semibold mb-6" data-testid="seva-back"><ArrowLeft className="w-4 h-4" /> All Sevas</Link>
        <div className="grid md:grid-cols-2 gap-10">
          <img src={seva.image} alt={seva.name} className="rounded-3xl shadow-xl w-full h-[420px] object-cover" />
          <div>
            <span className="text-sm font-semibold text-amber-700">{seva.category_name}</span>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-stone-900 mt-1">{seva.name}</h1>
            <div className="flex items-center gap-4 mt-3 text-stone-600">
              <span className="text-2xl font-extrabold text-orange-700">{money(s, seva.price)}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {seva.duration}</span>
            </div>
            <p className="text-lg text-stone-600 mt-5">{seva.short_description}</p>
y                       <p className="text-stone-600 mt-2 leading-relaxed whitespace-pre-line">{seva.description}</p>
            <Link to={`/booking?seva=${seva.id}`} className="inline-flex items-center gap-2 mt-7 bg-orange-600 hover:bg-orange-700 text-white font-bold px-7 py-4 rounded-full shadow-lg transition-colors" data-testid="seva-book-now">
              <Flower2 className="w-5 h-5" /> Book this Seva
            </Link>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg text-stone-900 mb-2">Instructions</h3>
            <p className="text-stone-600">{seva.instructions}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg text-stone-900 mb-2 flex items-center gap-2"><Info className="w-4 h-4" /> Cancellation & Refund</h3>
            <p className="text-stone-600">{seva.cancellation_rules}</p>
          </div>
        </div>
      </Section>
    </div>
  );
}
